/**
 * Batch-queue panel for the public "Send a Case" form.
 *
 * UX model:
 *   1. Doctor fills in the form for case #1, then clicks "+ Add another to
 *      batch". The case is validated, queued as a summary card here, and the
 *      per-case fields (patient / product / teeth / files / notes) reset
 *      while common fields (clinic / dentist / email / phone / order_date)
 *      stay in place for the next case.
 *   2. Doctor can review queued cases, remove any, or "Same as last patient"
 *      to copy the previous patient ID / name into the new card.
 *   3. The Submit button at the bottom of the form re-labels to
 *      "Submit N cases" and fires them sequentially on click.
 *
 * iter 56 — international standard for batch entry (analogous to multi-item
 * invoices in QuickBooks / Xero / Stripe Invoice). Each case becomes its own
 * row in `cases` on the backend with its own case_number + auto-generated
 * purchase_order_number — clean separation, no batch-level coupling.
 */
import { Trash2, User, Package, Layers, Files } from "lucide-react";

export function BatchQueuePanel({ queue, onRemove, onSameAsLast, sameAsLastActive }) {
    if (!queue || queue.length === 0) return null;
    const last = queue[queue.length - 1];
    return (
        <div
            data-testid="batch-queue-panel"
            className="rounded-2xl border border-cyan-200 bg-cyan-50/70 p-5 mb-6"
        >
            <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <Layers size={16} className="text-cyan-700" />
                    <div className="font-mono-tr text-[11px] tracking-[0.16em] uppercase text-cyan-900 font-semibold">
                        Batch · {queue.length} case{queue.length !== 1 ? "s" : ""} ready
                    </div>
                </div>
                <label className="flex items-center gap-2 text-[12.5px] text-cyan-900 cursor-pointer select-none" data-testid="same-as-last-wrap">
                    <input
                        type="checkbox"
                        checked={!!sameAsLastActive}
                        onChange={(e) => onSameAsLast(e.target.checked, last)}
                        className="h-3.5 w-3.5 rounded border-cyan-400 text-cyan-700 focus:ring-cyan-300"
                        data-testid="same-as-last-checkbox"
                    />
                    Same patient as last queued case
                </label>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {queue.map((c, i) => (
                    <div
                        key={c.uid}
                        data-testid={`queued-case-${i}`}
                        className="bg-white rounded-xl border border-cyan-100 px-3.5 py-2.5 flex items-start gap-2.5 group"
                    >
                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-100 text-cyan-800 font-mono-tr text-[11px] font-semibold flex items-center justify-center">
                            {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium text-slate-900 truncate flex items-center gap-1.5">
                                <User size={11} className="text-slate-400" />
                                {c.patient_name || c.patient_id || "Patient"}
                            </div>
                            <div className="text-[11.5px] text-slate-500 truncate flex items-center gap-1.5 mt-0.5">
                                <Package size={10} className="text-slate-400" />
                                {c.productLabel || c.product}
                                {c.toothCount > 0 && <span className="text-slate-400">· {c.toothCount} unit{c.toothCount !== 1 ? "s" : ""}</span>}
                                {c.fileCount > 0 && (
                                    <span className="text-slate-400 inline-flex items-center gap-0.5">
                                        · <Files size={9} /> {c.fileCount}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => onRemove(i)}
                            data-testid={`remove-queued-${i}`}
                            aria-label="Remove from batch"
                            className="opacity-50 group-hover:opacity-100 hover:text-rose-500 text-slate-400 transition flex-shrink-0"
                        >
                            <Trash2 size={13} />
                        </button>
                    </div>
                ))}
            </div>

            <div className="mt-3 text-[11.5px] text-cyan-800/85 leading-snug">
                Each queued case will get its own <strong>case number</strong> and <strong>PO number</strong> when you submit — they're tracked independently.
            </div>
        </div>
    );
}
